import React, { useEffect, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { motion } from 'framer-motion';
import { Waves, Clock, Users, MapPin, ShieldCheck, Camera, Sun, Anchor, CheckCircle2, Package } from 'lucide-react';
import { Button } from '../components/ui/Button';
const highlights = [{
  icon: ShieldCheck,
  title: 'הדרכה צמודה',
  description: 'מדריך מוסמך ומנוסה מלווה אתכם לאורך כל הדרך'
}, {
  icon: Waves,
  title: 'חתירה רגועה',
  description: 'קצב נינוח המותאם לקבוצה, ללא מאמץ מיותר'
}, {
  icon: Camera,
  title: 'נופים מרהיבים',
  description: 'הזדמנות לראות את הכנרת מזווית אחרת ושקטה'
}, {
  icon: Package,
  title: 'הכל כלול',
  description: 'גלשן סאפ איכותי, משוט, חגורת הצלה והדרכה'
}];
const audience = ['משפחות המחפשות חוויה משותפת', 'זוגות ביום כיף רומנטי', 'קבוצות חברים', 'מי שרוצה לנסות סאפ לראשונה בסביבה בטוחה', 'מי שמחפש שקט, טבע וחיבור למים'];
export function SUPToursPage() {
  useEffect(() => {
    // SEO Meta Tags
    document.title = "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'טיולי סאפ מודרכים בכנרת - חוויה מרגיעה של שעה וחצי, מתאימה לכולם, ללא צורך בניסיון קודם. כולל ציוד והדרכה צמודה.');
    // Open Graph with image
    const ogTags = [{
      property: 'og:title',
      content: "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן"
    }, {
      property: 'og:description',
      content: 'טיולי סאפ מודרכים בכנרת - חוויה מרגיעה וקסומה לכל המשפחה.'
    }, {
      property: 'og:type',
      content: 'website'
    }, {
      property: 'og:image',
      content: 'https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg'
    }, {
      property: 'og:image:width',
      content: '1200'
    }, {
      property: 'og:image:height',
      content: '630'
    }, {
      property: 'og:url',
      content: window.location.href
    },
    // Twitter Card tags
    {
      property: 'twitter:card',
      content: 'summary_large_image'
    }, {
      property: 'twitter:title',
      content: "טיולי סאפ מודרכים בכנרת | ביץ' קלאב מעגן"
    }, {
      property: 'twitter:description',
      content: 'חוויה מרגיעה של שעה וחצי על מי הכנרת'
    }, {
      property: 'twitter:image',
      content: 'https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg'
    }];
    ogTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', tag.property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
  }, []);
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-1979" data-mp="k30umpp9hxap">
      <Navigation data-id="element-1980" data-mp="p0ivdst4l67m" />

      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden" data-id="element-1981" data-mp="a2i3d9vdbeir">
        <div className="absolute inset-0 bg-black/40 z-10" data-id="element-1982" data-mp="a41kf2oultll" />
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/hhg6LtFE9yaLy4GHP6mfvz/20240328_164359.jpg" alt="SUP Tour Kinneret" className="w-full h-full object-cover" data-id="element-1983" data-mp="f11pba9yoqb4" />
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center text-white px-4" data-id="element-1984" data-mp="12tibnx0qf4g8">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl font-bold mb-4" data-id="element-1985" data-mp="t1mcsjqtnaay">
            טיולי סאפ מודרכים
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl md:text-2xl max-w-2xl font-light" data-id="element-1986" data-mp="1logsl3cp1li3">
            לגלות את הכנרת בקצב שלכם - חוויה שקטה ומרגיעה על המים
          </motion.p>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#0A5F8F] text-white py-8 relative z-30 -mt-8 mx-4 md:mx-auto max-w-5xl rounded-2xl shadow-xl" data-id="element-1987" data-mp="3p0ofz1a7ae85">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-white/20 divide-x-reverse" data-id="element-1988" data-mp="3k23fr1uy7ujp">
          <div className="flex flex-col items-center gap-2" data-id="element-1989" data-mp="lkzes951jrfq">
            <Clock className="w-6 h-6 text-cyan-300" data-id="element-1990" data-mp="gkv1erzfjctq" />
            <span className="font-bold text-lg" data-id="element-1991" data-mp="bt3gawz1lzaz">משך הטיול</span>
            <span className="text-blue-100" data-id="element-1992" data-mp="brijf0p9a49y">כשעה וחצי</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-1993" data-mp="6sz1dbds9cmv">
            <Users className="w-6 h-6 text-cyan-300" data-id="element-1994" data-mp="149e9p7ihbplt" />
            <span className="font-bold text-lg" data-id="element-1995" data-mp="14ayzykxrgmdv">למי מתאים?</span>
            <span className="text-blue-100" data-id="element-1996" data-mp="zifobtimw1bp">לרוב האנשים</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-1997" data-mp="17rbqcmctemyg">
            <ShieldCheck className="w-6 h-6 text-cyan-300" data-id="element-1998" data-mp="17t4vfx7s4z4c" />
            <span className="font-bold text-lg" data-id="element-1999" data-mp="1bs5cba6p5rop">ניסיון קודם</span>
            <span className="text-blue-100" data-id="element-2000" data-mp="1gs021aeqkb86">לא נדרש</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-id="element-2001" data-mp="1gqp07g4updjp">
            <Package className="w-6 h-6 text-cyan-300" data-id="element-2002" data-mp="1lrcy8lrhyunf" />
            <span className="font-bold text-lg" data-id="element-2003" data-mp="1loqivzyd7aun">מה כלול?</span>
            <span className="text-blue-100" data-id="element-2004" data-mp="1qpeez01gahvq">ציוד מלא + הדרכה</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-20" data-id="element-2005" data-mp="1upexavro7d82">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-2006" data-mp="1zjj7dnw8g1dd">
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-2007" data-mp="24hufnu6vq3th">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-6" data-id="element-2008" data-mp="2ef8ujpd5mkjo">
              חוויה בלתי נשכחת על המים
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-2009" data-mp="1os4owt0apkm1">
              טיולי הסאפ שלנו מציעים הזדמנות ייחודית להתנתק מהרעש של היומיום
              ולהתחבר לשקט של הכנרת. אנחנו יוצאים בקבוצות קטנות ואינטימיות,
              מלווים במדריך מוסמך שדואג לכל פרט.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-2010" data-mp="1tpxwnqh8ymof">
              הטיול מתחיל בהדרכה קצרה על החוף, בה נלמד את עקרונות החתירה
              הבסיסיים, עמידה נכונה ובטיחות. לאחר מכן נצא למים לחתירה רגועה
              לאורך החוף, נהנה מהנוף, מהאוויר ומהשלווה.
            </p>

            <Button size="lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-2011" data-mp="2hf8x16uoqf21">
              הזמינו טיול עכשיו
            </Button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-2012" data-mp="n2stwex6rh1e">
            <img src="https://cdn.magicpatterns.com/uploads/1FrmywPxsyJQJu2FnAtQFR/IMG-20251203-WA0011.jpg" alt="טיול סאפ בכנרת" className="w-full h-[400px] object-cover rounded-3xl shadow-xl" data-id="element-2013" data-mp="2tsmhnvjhp7xz" />
          </motion.div>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-id="element-2014" data-mp="2oyrqy85gvspe">
          {highlights.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-blue-50 p-6 rounded-2xl border border-blue-100 hover:shadow-md transition-shadow" data-id="element-2015" data-mp="2aygzp408wc3p" data-mpi={index}>
              <item.icon className="w-10 h-10 text-[#0A5F8F] mb-4" data-id="element-2016" data-mp="3vfdje90da3wh" />
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2017" data-mp="3ve2f8830eu4u">
                {item.title}
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-2018" data-mp="2n7vjyjd39t2s">{item.description}</p>
            </motion.div>)}
        </div>

        {/* Who is this for Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-10 md:p-16 mb-20" data-id="element-2019" data-mp="2j71wts7h0iay">
          <div className="text-center mb-12" data-id="element-2020" data-mp="24i5dv03vlkvk">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A5F8F] mb-4" data-id="element-2021" data-mp="24j6z1xelvnr0">
              למי הטיול מתאים?
            </h2>
            <p className="text-xl text-gray-600" data-id="element-2022" data-mp="1zkvplns83f9b">התשובה הקצרה: כמעט לכולם!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-2023" data-mp="1visex1ddoep3">
            {audience.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-xl shadow-sm flex items-center gap-4" data-id="element-2024" data-mp="3pvi7nfs0fhuw" data-mpi={index}>
                <div className="bg-green-100 p-2 rounded-full shrink-0" data-id="element-2025" data-mp="170pv6eh1j3d">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-2026" data-mp="60c0zjqbgm13" />
                </div>
                <span className="text-lg font-medium text-gray-800" data-id="element-2027" data-mp="axlmwpehyfk6">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </motion.div>

        {/* What to bring */}
        <div className="max-w-4xl mx-auto text-center mb-20" data-id="element-2028" data-mp="3eg5gpwctpth3">
          <h2 className="text-3xl font-bold text-[#0A5F8F] mb-8" data-id="element-2029" data-mp="3je6z0f2fdori">
            מה צריך להביא?
          </h2>
          <div className="flex flex-wrap justify-center gap-8" data-id="element-2030" data-mp="3ngk3l05frgmp">
            {[{
            icon: Sun,
            text: 'קרם הגנה'
          }, {
            icon: Anchor,
            text: 'בגד ים'
          }, {
            icon: Package,
            text: 'מגבת'
          }, {
            icon: Camera,
            text: 'מצלמה (אופציונלי)'
          }].map((item, index) => <div key={index} className="flex flex-col items-center gap-3" data-id="element-2031" data-mp="1y7a9njvly1ag" data-mpi={index}>
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-2032" data-mp="1y579w84nktsj">
                  <item.icon className="w-8 h-8 text-gray-600" data-id="element-2033" data-mp="2303d84fcb3ky" />
                </div>
                <span className="font-medium text-gray-700" data-id="element-2034" data-mp="27wbov7ubwend">{item.text}</span>
              </div>)}
          </div>
        </div>

        {/* CTA Box */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-[#0A5F8F] rounded-3xl p-12 text-center text-white relative overflow-hidden" data-id="element-2035" data-mp="1f7wdqwebighb">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-2036" data-mp="1p4953nme4i8w"></div>
          <div className="relative z-10" data-id="element-2037" data-mp="30liqwgszrmdd">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2038" data-mp="30k7iu4sijsgg">
              מוכנים לטיול בלתי נשכח?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto" data-id="element-2039" data-mp="p0i3c3gjhax5">
              הצטרפו אלינו לחוויה קסומה על המים. מספר המקומות בכל טיול מוגבל כדי
              לשמור על יחס אישי.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2040" data-mp="f6j8zqmpivfr">
              <Button size="lg" className="bg-white text-[#0A5F8F] hover:bg-blue-50 border-none shadow-lg" onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-2041" data-mp="a8zqufmw1yij">
                <span className="text-[#0A5F8F] font-bold" data-id="element-2042" data-mp="1b7ar99cemu3">
                  שריינו מקום עכשיו
                </span>
              </Button>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm" onClick={() => window.location.href = 'tel:0522632784'} data-id="element-2043" data-mp="3nguk0hiav2q5">
                חייגו לפרטים: 052-2632784
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      <ContactSection data-id="element-2044" data-mp="3g44p8b5zmkmu" />
      <Footer data-id="element-2045" data-mp="3g3lf3gmkx0jd" />
    </div>;
}